import React from 'react';
import PropTypes from 'prop-types';
import ThumbnailErrorBoundary from './ThumbnailErrorBoundary';
import { getThumbnailUrl, resolveVideoId } from '../utils/videoUtils.js';
import { WEB3FORMS_ACCESS_KEY } from '../config.js';

/**
 * VideoCard component for displaying individual video thumbnails
 * @param {Object} props - Component props
 * @param {Object} props.video - Video data object
 * @param {Function} props.onClick - Click handler function
 * @param {string} props.loading - Image loading strategy ('lazy' | 'eager'), defaults to 'lazy'
 */
const VideoCard = ({ video, onClick, loading = 'lazy' }) => {
  const [showReportMenu, setShowReportMenu] = React.useState(false);
  const [reportStatus, setReportStatus] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const reportButtonRef = React.useRef(null);
  const reportMenuRef = React.useRef(null);
  const autoCloseTimeoutRef = React.useRef(null);

  const title = video.snippet?.title || video.title || 'Video Title';
  const channelTitle = video.snippet?.channelTitle || video.channelTitle || 'Channel';
  const channelId = video.snippet?.channelId || video.channelId || '';
  const videoId = resolveVideoId(video.id || video);

  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
  const channelUrl = channelId ? `https://www.youtube.com/channel/${channelId}` : null;

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showReportMenu &&
        reportMenuRef.current &&
        !reportMenuRef.current.contains(event.target) &&
        reportButtonRef.current &&
        !reportButtonRef.current.contains(event.target)
      ) {
        setShowReportMenu(false);
        setReportStatus(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clear auto-close timeout on unmount
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, [showReportMenu]);

  const handleReportClick = (e) => {
    e.stopPropagation();
    setShowReportMenu(!showReportMenu);
    setReportStatus(null);
  };

  const handleReportSubmit = async (reason) => {
    // Validate videoId before submitting
    if (!videoId) {
      setReportStatus({
        type: 'error',
        message: 'Unable to report this video. Video ID is missing.',
      });
      return;
    }

    setIsSubmitting(true);
    setReportStatus(null);

    try {
      const formData = new FormData();
      formData.append('access_key', WEB3FORMS_ACCESS_KEY);
      formData.append('subject', 'Video Report');
      formData.append('video_id', videoId || '');
      formData.append('video_title', title || '');
      formData.append('video_url', watchUrl || '');
      formData.append('report_reason', reason);
      formData.append('message', `Report: ${reason}\nVideo ID: ${videoId}\nVideo Title: ${title}\nVideo URL: ${watchUrl || ''}`);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setReportStatus({
          type: 'success',
          message: 'Thank you! Your report has been submitted.',
        });
        // Clear any existing timeout before setting a new one
        if (autoCloseTimeoutRef.current) {
          clearTimeout(autoCloseTimeoutRef.current);
        }
        autoCloseTimeoutRef.current = setTimeout(() => {
          setShowReportMenu(false);
          setReportStatus(null);
        }, 2000);
      } else {
        setReportStatus({
          type: 'error',
          message: result.message || 'Something went wrong. Please try again.',
        });
      }
    } catch (error) {
      setReportStatus({
        type: 'error',
        message: 'Unable to submit the report. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const reportReasons = [
    'Illegal Content',
    'Offensive Content',
    'Link Broken',
    'Not ASMR',
  ];

  return (
    <div className="col">
      <div
        className="card h-100 cursor-pointer hover-shadow"
        onClick={() => onClick(video)}
      >
        <div className="video-thumbnail-container">
          <ThumbnailErrorBoundary>
            {/* ⚡ Bolt: Added lazy loading to thumbnail image below the fold */}
            <img
              src={getThumbnailUrl(video)}
              className="video-thumbnail"
              alt={title}
              loading={loading}
              onError={(e) => {
                const src = e.target.src;
                if (src.includes('hqdefault.jpg')) {
                  e.target.src = src.replace('hqdefault.jpg', 'mqdefault.jpg');
                } else if (!src.endsWith('placeholder-image.jpg')) {
                  e.target.onerror = null;
                  e.target.src = 'placeholder-image.jpg';
                }
              }}
            />
          </ThumbnailErrorBoundary>
          <div className="play-icon">
            <i className="fas fa-play-circle"></i>
          </div>
          <button
            ref={reportButtonRef}
            type="button"
            className="report-button"
            onClick={handleReportClick}
            aria-label="Flag for site admin"
            title="Flag for site admin"
          >
            <i className="fas fa-flag"></i>
          </button>
          {showReportMenu && (
            <div
              className="report-menu"
              ref={reportMenuRef}
              onClick={(e) => e.stopPropagation()}
            >
              {reportStatus ? (
                <div className={`report-status ${reportStatus.type}`}>
                  {reportStatus.message}
                </div>
              ) : (
                <>
                  <div className="report-menu-header">Flag for Site Admin</div>
                  <div className="report-menu-disclaimer">
                    This flags the video for our directory moderators. To report a Community Guidelines violation, watch the video on YouTube and use their native reporting tool.
                  </div>
                  {reportReasons.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className="report-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReportSubmit(reason);
                      }}
                      disabled={isSubmitting}
                    >
                      {reason}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        <div className="card-body">
          <h5 className="card-title">{title}</h5>
          <p className="card-text">
            <i className="fas fa-user-circle me-2"></i>
            {channelUrl ? (
              <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted"
              >
                {channelTitle}
              </a>
            ) : channelTitle}
          </p>
          {watchUrl && (
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="watch-on-youtube-link"
              aria-label={`Watch "${title}" on YouTube`}
            >
              Watch on YouTube
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

VideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    title: PropTypes.string,
    channelTitle: PropTypes.string,
    channelId: PropTypes.string,
    snippet: PropTypes.shape({
      thumbnails: PropTypes.shape({
        high: PropTypes.shape({ url: PropTypes.string }),
        medium: PropTypes.shape({ url: PropTypes.string }),
      }),
      title: PropTypes.string,
      channelTitle: PropTypes.string,
      channelId: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  loading: PropTypes.oneOf(['lazy', 'eager']),
};

// ⚡ Bolt: Wrapped VideoCard in React.memo to prevent unnecessary re-renders
// for potentially hundreds of cards when unrelated parent state changes.
export default React.memo(VideoCard);
